import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { get } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiTile, KpiGrid, Toolbar, FilterToggle } from "@/components/common/Page";
import { LoadingState, ErrorState } from "@/components/common/States";
import { EnumSelect } from "@/components/common/Pickers";
import { TransportFormSheet, ServiceJobFormSheet, IssueFormSheet, TaskFormSheet } from "@/components/ops/OpsForms";
import { TaskDetailSheet, TransportDetailSheet, ServiceDetailSheet, IssueDetailSheet, TaskList, TransportList, ServiceList, IssueList } from "@/components/ops/DetailSheets";
import { SERVICE_STATUSES, ISSUE_STATUSES, ISSUE_SEVERITIES, TRANSPORT_STATUSES, TASK_STATUSES, label } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, X } from "lucide-react";

export default function ServicePage() {
  const { can, user } = useAuth();
  const { filters, setFilter, setMany, reset, active } = useFilters("service", { tab: "service", status: "all", severity: "all", mine: "", overdue: "" });
  const tab = ["service", "avvik", "transport", "oppgaver"].includes(filters.tab) ? filters.tab : "service";
  const [create, setCreate] = useState(null);
  const [detail, setDetail] = useState(null);
  const [params, setParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);

  // Deep link: /service?tab=transport&id=<id> opens the detail sheet once.
  useEffect(() => {
    const id = params.get("id");
    if (!id) return;
    const type = { service: "service", avvik: "issue", transport: "transport", oppgaver: "task" }[tab];
    setDetail({ type, id });
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next, { replace: true });
  }, [params, setParams, tab]);

  const svc = useQuery({ queryKey: ["service-jobs"], queryFn: () => get("/service-jobs") });
  const issues = useQuery({ queryKey: ["issues"], queryFn: () => get("/issues") });
  const transports = useQuery({ queryKey: ["transports"], queryFn: () => get("/transports") });
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => get("/tasks") });

  const openSvc = (svc.data || []).filter((s) => !["fullfort", "avbrutt"].includes(s.status));
  const overdueSvc = openSvc.filter((s) => s.due_date && s.due_date < today);
  const openIssues = (issues.data || []).filter((i) => ["ny", "under_behandling"].includes(i.status));
  const unconfirmed = (transports.data || []).filter((t) => ["planlagt", "bestilt"].includes(t.status) && t.date >= today);
  const openTasks = (tasks.data || []).filter((t) => !["fullfort", "avbrutt"].includes(t.status));
  const overdueTasks = openTasks.filter((t) => t.due_date && t.due_date < today);

  const mineFilter = (x) => !filters.mine || x.assigned_to === user?.id || x.reported_by === user?.id;
  const statusFilter = (x) => filters.status === "all" || x.status === filters.status;
  const lists = {
    service: (svc.data || []).filter(statusFilter).filter(mineFilter).filter((s) => !filters.overdue || (s.due_date && s.due_date < today && !["fullfort", "avbrutt"].includes(s.status))),
    avvik: (issues.data || []).filter(statusFilter).filter(mineFilter).filter((i) => filters.severity === "all" || i.severity === filters.severity),
    transport: (transports.data || []).filter(statusFilter).filter(mineFilter),
    oppgaver: (tasks.data || []).filter(statusFilter).filter(mineFilter).filter((t) => !filters.overdue || (t.due_date && t.due_date < today && !["fullfort", "avbrutt"].includes(t.status))),
  };
  const STATUSES = { service: SERVICE_STATUSES, avvik: ISSUE_STATUSES, transport: TRANSPORT_STATUSES, oppgaver: TASK_STATUSES };
  const q = { service: svc, avvik: issues, transport: transports, oppgaver: tasks }[tab];
  const createLabel = { service: ["service", "Nytt serviceoppdrag", "service:write"], avvik: ["issue", "Registrer avvik", "issues:write"], transport: ["transport", "Planlegg transport", "transport:write"], oppgaver: ["task", "Ny oppgave", "tasks:write"] }[tab];

  return (
    <div className="page-pad space-y-4 fade-up" data-testid="service-page">
      <PageHeader
        title="Drift og service"
        subtitle="Serviceoppdrag, avvik, transport og oppgaver på tvers av prosjekter"
        actions={
          can(createLabel[2]) && (
            <Button onClick={() => setCreate(createLabel[0])} data-testid="service-create-button">
              <Plus /> {createLabel[1]}
            </Button>
          )
        }
      />
      <KpiGrid>
        <KpiTile label="Service forfalt" value={overdueSvc.length} hint={`${openSvc.length} åpne oppdrag`} tone={overdueSvc.length ? "warning" : "default"} onClick={() => setMany({ tab: "service", overdue: "1", status: "all" })} testId="kpi-tile-service-overdue" />
        <KpiTile label="Åpne avvik" value={openIssues.length} hint={`${openIssues.filter((i) => ["hoy", "kritisk"].includes(i.severity)).length} høy/kritisk`} tone={openIssues.some((i) => i.severity === "kritisk") ? "danger" : "default"} onClick={() => setMany({ tab: "avvik", status: "all" })} testId="kpi-tile-issues-open" />
        <KpiTile label="Transport ubekreftet" value={unconfirmed.length} hint={`${(transports.data || []).filter((t) => t.date >= today && t.status !== "fullfort").length} kommende`} tone={unconfirmed.length ? "warning" : "default"} onClick={() => setMany({ tab: "transport", status: "planlagt" })} testId="kpi-tile-transport-unconfirmed" />
        <KpiTile label="Oppgaver forfalt" value={overdueTasks.length} hint={`${openTasks.length} åpne`} tone={overdueTasks.length ? "warning" : "default"} onClick={() => setMany({ tab: "oppgaver", overdue: "1", status: "all" })} testId="kpi-tile-tasks-overdue" />
      </KpiGrid>

      <Tabs value={tab} onValueChange={(v) => setMany({ tab: v, status: "all", overdue: "" })}>
        <TabsList data-testid="service-tabs">
          <TabsTrigger value="service" data-testid="service-tab-service">
            Service <span className="text-[11.5px] text-muted-foreground tabular">{openSvc.length}</span>
          </TabsTrigger>
          <TabsTrigger value="avvik" data-testid="service-tab-avvik">
            Avvik <span className="text-[11.5px] text-muted-foreground tabular">{openIssues.length}</span>
          </TabsTrigger>
          <TabsTrigger value="transport" data-testid="service-tab-transport">
            Transport <span className="text-[11.5px] text-muted-foreground tabular">{(transports.data || []).filter((t) => t.status !== "fullfort").length}</span>
          </TabsTrigger>
          <TabsTrigger value="oppgaver" data-testid="service-tab-oppgaver">
            Oppgaver <span className="text-[11.5px] text-muted-foreground tabular">{openTasks.length}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Toolbar>
        <EnumSelect value={filters.status} onChange={(v) => setFilter("status", v)} options={STATUSES[tab]} labelFn={label} allLabel="Alle statuser" className="h-8 w-44" testId="service-status-filter" />
        {tab === "avvik" && <EnumSelect value={filters.severity} onChange={(v) => setFilter("severity", v)} options={ISSUE_SEVERITIES} labelFn={label} allLabel="Alle alvorlighetsgrader" className="h-8 w-48" testId="issue-severity-filter" />}
        <FilterToggle pressed={!!filters.mine} onClick={() => setFilter("mine", filters.mine ? "" : "1")} testId="service-mine-toggle">
          Mine
        </FilterToggle>
        {(tab === "service" || tab === "oppgaver") && (
          <FilterToggle pressed={!!filters.overdue} onClick={() => setFilter("overdue", filters.overdue ? "" : "1")} testId="service-overdue-toggle">
            Kun forfalt
          </FilterToggle>
        )}
        {active > 0 && (
          <Button variant="ghost" onClick={reset} data-testid="filters-reset-button">
            <X className="h-3.5 w-3.5" /> Nullstill
          </Button>
        )}
        <span className="ml-auto text-[12px] text-muted-foreground tabular" data-testid="service-row-count">
          {lists[tab].length} rader
        </span>
      </Toolbar>

      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={q.refetch} />
      ) : (
        <div data-testid={`service-list-${tab}`}>
          {tab === "service" && <ServiceList items={lists.service} onOpen={(s) => setDetail({ type: "service", id: s.id })} emptyText="Ingen serviceoppdrag med gjeldende filter" />}
          {tab === "avvik" && <IssueList items={lists.avvik} onOpen={(i) => setDetail({ type: "issue", id: i.id })} emptyText="Ingen avvik med gjeldende filter" />}
          {tab === "transport" && <TransportList items={lists.transport} onOpen={(t) => setDetail({ type: "transport", id: t.id })} emptyText="Ingen transporter med gjeldende filter" />}
          {tab === "oppgaver" && <TaskList tasks={lists.oppgaver} onOpen={(t) => setDetail({ type: "task", id: t.id })} emptyText="Ingen oppgaver med gjeldende filter" />}
        </div>
      )}
      <ServiceJobFormSheet open={create === "service"} onOpenChange={(o) => !o && setCreate(null)} />
      <IssueFormSheet open={create === "issue"} onOpenChange={(o) => !o && setCreate(null)} />
      <TransportFormSheet open={create === "transport"} onOpenChange={(o) => !o && setCreate(null)} />
      <TaskFormSheet open={create === "task"} onOpenChange={(o) => !o && setCreate(null)} />
      <TaskDetailSheet taskId={detail?.type === "task" ? detail.id : null} open={detail?.type === "task"} onOpenChange={(o) => !o && setDetail(null)} />
      <TransportDetailSheet transportId={detail?.type === "transport" ? detail.id : null} open={detail?.type === "transport"} onOpenChange={(o) => !o && setDetail(null)} />
      <ServiceDetailSheet serviceId={detail?.type === "service" ? detail.id : null} open={detail?.type === "service"} onOpenChange={(o) => !o && setDetail(null)} />
      <IssueDetailSheet issueId={detail?.type === "issue" ? detail.id : null} open={detail?.type === "issue"} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
